import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertTriangle, Plug } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Footer } from '@/components/footer';
import { ErrorState, ListSkeleton } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/lib/auth';
import { consentRedirect } from '@/lib/oauth';

const WRITE_SCOPE = 'outpost:write';

/**
 * The OAuth consent screen.
 *
 * The authorization endpoint parks a request and sends the browser here. This
 * page is the only point at which a human decides whether an application may
 * read their production errors, so it has to describe the grant accurately
 * rather than reassuringly: which application, where approving sends the
 * browser, and which organizations are covered.
 */
export function OAuthConsentPage() {
  const [params] = useSearchParams();
  const { client, user } = useAuth();

  const requestId = params.get('request_id') ?? '';
  const authorizeError = params.get('error');
  // Off until the user turns it on, whatever the application asked for: reading
  // is what connecting an assistant usually means, and changes are opted into.
  const [allowWrite, setAllowWrite] = useState(false);

  const request = useQuery({
    queryKey: ['oauth-consent', requestId],
    queryFn: () => client.getConsentRequest(requestId),
    enabled: requestId !== '' && !authorizeError,
    retry: false,
  });

  const decide = useMutation({
    mutationFn: async (approve: boolean) => {
      const result = await client.decideConsent(requestId, approve, approve && allowWrite);
      const destination = consentRedirect(result.redirect_to);
      if (!destination) {
        throw new Error('The application asked to be sent somewhere that is not a web address.');
      }
      return destination;
    },
    onSuccess: (destination) => {
      // A full navigation, not a router push: the destination belongs to the
      // client application, not to the dashboard.
      window.location.href = destination;
    },
  });

  // The authorization endpoint could not send this error back to the client —
  // usually an unregistered redirect URI — so it lands here instead.
  if (authorizeError) {
    return (
      <Shell>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="size-4 text-destructive" />
              This connection request was refused
            </CardTitle>
            <CardDescription>{params.get('error_description') ?? authorizeError}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Nothing has been granted. Start the connection again from the application, and if it
              keeps failing, check that its callback URL matches the one it registered.
            </p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  if (!requestId) {
    return (
      <Shell>
        <ErrorState error={new Error('This page needs a connection request to act on.')} />
      </Shell>
    );
  }

  if (request.isLoading) {
    return (
      <Shell>
        <ListSkeleton rows={2} />
      </Shell>
    );
  }

  if (request.isError) {
    return (
      <Shell>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">This request is no longer valid</CardTitle>
            <CardDescription>
              Connection requests expire after ten minutes, and each can be answered only once.
              Start again from the application.
            </CardDescription>
          </CardHeader>
        </Card>
      </Shell>
    );
  }

  const details = request.data;
  if (!details) return null;

  const redirectHost = hostOf(details.redirect_uri);
  const offersWrite = details.scopes.includes(WRITE_SCOPE);

  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Plug className="size-4" />
            Connect {details.client_name}?
          </CardTitle>
          <CardDescription>
            {details.client_name} is asking to {offersWrite ? 'use' : 'read'} your Outpost data as{' '}
            <span className="font-medium text-foreground">{user?.email}</span>.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h2 className="text-sm font-medium">It will be able to</h2>
            <p className="text-sm text-muted-foreground">
              Read your projects, issues, events and releases — everything you can see in this
              dashboard.
            </p>
            {offersWrite && (
              <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 shrink-0 accent-control"
                  checked={allowWrite}
                  onChange={(event) => setAllowWrite(event.currentTarget.checked)}
                />
                <span className="space-y-1">
                  <span className="block font-medium">Also allow it to make changes</span>
                  <span className="block text-muted-foreground">
                    Resolve, ignore and assign issues, and write comments, as you — never more than
                    your role allows. Its changes are shown to your colleagues as made through{' '}
                    {details.client_name}.
                  </span>
                </span>
              </label>
            )}
          </div>

          {details.organizations.length > 0 && (
            <div className="space-y-1">
              <h2 className="text-sm font-medium">Across</h2>
              {/* Stated plainly: the grant is not scoped to one project, and a
                  user deciding this deserves to know that before approving. */}
              <p className="text-sm text-muted-foreground">
                {details.organizations.join(', ')} — every project you can see in{' '}
                {details.organizations.length === 1 ? 'it' : 'them'}.
              </p>
            </div>
          )}

          <div className="space-y-1">
            <h2 className="text-sm font-medium">After approving, you will be sent to</h2>
            <p className="break-all text-sm text-muted-foreground">
              <code>{redirectHost}</code>
            </p>
            <p className="text-xs text-muted-foreground">
              If you did not just start this from {details.client_name}, decline.
            </p>
          </div>

          <p className="text-xs text-muted-foreground">
            {allowWrite
              ? 'Anything it changes, it changes in your name.'
              : 'This application will not be able to change anything: access is read-only.'}{' '}
            You can disconnect it at any time from your user settings, and that takes effect
            immediately.
          </p>

          {decide.isError && (
            <p className="text-sm text-destructive">
              {(decide.error as Error).message || 'That did not work. Try again.'}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <Button onClick={() => decide.mutate(true)} disabled={decide.isPending}>
              Approve
            </Button>
            <Button
              variant="ghost"
              onClick={() => decide.mutate(false)}
              disabled={decide.isPending}
            >
              Decline
            </Button>
          </div>
        </CardContent>
      </Card>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col px-4">
      <main className="grid flex-1 place-items-center py-10">
        <div className="w-full max-w-lg">{children}</div>
      </main>
      <Footer />
    </div>
  );
}

/** Show the origin rather than the whole URL: it is what identifies the target. */
function hostOf(uri: string): string {
  try {
    return new URL(uri).origin;
  } catch {
    return uri;
  }
}
